import { useCallback, useRef, useState } from "react"
import Downshift, { DownshiftState, ControllerStateAndHelpers, StateChangeOptions } from "downshift"
import * as T from "trunx"
import cn from "classnames"
import { Div } from "trunx"

import { AutocompleteItem, AutocompleteProps } from "./interface"

import { Input } from "ui-kit/components-branded/FormControls/Input"
import { Text } from "ui-kit/components-branded/Text"
import { useToggleClassOnHover } from "ui-kit/components-branded/hooks/setHoverHandler"
import { useQuerySelector } from "ui-kit/components-branded/hooks/useQuerySelector"
import { useFitHeight } from "ui-kit/components-branded/hooks/useFitHeight"
import { Button } from "ui-kit/components-branded/FormControls/Button"
import { Icon } from "components"
import { isInputFocused } from "utils/helpers"

export const Autocomplete = <ItemType extends AutocompleteItem>({
    items,
    defaultItem,
    anyLabel,
    getValue,
    getUniqKey,
    onChange,
    className,
}: AutocompleteProps<ItemType>) => {
    const [nodeId, getNode] = useQuerySelector()
    const [isActive, setIsActive] = useState(false)
    const scrollableRef = useRef<HTMLDivElement>(null)

    useToggleClassOnHover(getNode, "is-hovered")
    useFitHeight({
        activate: isActive,
        nodeRef: scrollableRef,
        getStartNode: getNode,
    })

    const filterItems = useCallback(
        (input: string | null) =>
            items.filter((item) =>
                !input ? true : getValue(item).toLowerCase().includes(input.toLowerCase())
            ),
        [items, getValue]
    )

    const stateReducer = (
        state: DownshiftState<ItemType>,
        changes: StateChangeOptions<ItemType>
    ): Partial<DownshiftState<ItemType>> | StateChangeOptions<ItemType> => {
        switch (changes.type) {
            case Downshift.stateChangeTypes.clickButton:
                getNode()?.querySelector("input")?.focus()
                return { ...changes, isOpen: true }
            case Downshift.stateChangeTypes.keyDownSpaceButton:
                return {
                    ...changes,
                    type: Downshift.stateChangeTypes.changeInput,
                    isOpen: true,
                    inputValue: `${state.inputValue ?? ""} `,
                    highlightedIndex: state.highlightedIndex,
                }
            default:
                return changes
        }
    }

    return (
        <Downshift
            onChange={(selection) => onChange?.(selection ?? undefined)}
            itemToString={(item) => (item ? getValue(item) : "")}
            initialSelectedItem={defaultItem}
            stateReducer={stateReducer}
            onStateChange={({ isOpen }) => {
                if (typeof isOpen === "boolean") setIsActive(isOpen)
            }}
        >
            {({
                getRootProps,
                getToggleButtonProps,
                getInputProps,
                getMenuProps,
                getItemProps,
                highlightedIndex,
                selectedItem,
                isOpen,
                inputValue,
                reset,
                setState,
            }: ControllerStateAndHelpers<ItemType>) => {
                const showAny =
                    !isOpen && !selectedItem && !isInputFocused(getNode()?.querySelector("input"))

                const inputProps = getInputProps({
                    className: cn("blm-autocomplete__input", { "is-active": isOpen }),
                    placeholder: "Введите название города",
                    onBlur() {
                        if (!isOpen && !selectedItem) setState({ inputValue: anyLabel })
                    },
                })

                if (showAny) inputProps.value = anyLabel ?? ""

                const filteredItems = isOpen ? filterItems(inputValue) : []
                const showEmpty = isOpen && !filteredItems.length && inputValue?.length

                return (
                    <T.Dropdown
                        className={cn("blm-dropdown", className)}
                        isActive={isOpen || isInputFocused()}
                    >
                        <div
                            {...getRootProps({ refKey: "ref" }, { suppressRefError: true })}
                            className="blm-autocomplete__root"
                        >
                            <div
                                {...getToggleButtonProps({
                                    className: cn(
                                        "select blm-select blm-dropdown__trigger blm-autocomplete",
                                        { "is-arrowless": !!selectedItem }
                                    ),
                                    id: nodeId,
                                })}
                            >
                                <Input {...inputProps} />
                                <div
                                    className="blm-autocomplete__icon"
                                    onClick={() => {
                                        reset()
                                        onChange?.()
                                    }}
                                >
                                    {selectedItem ? (
                                        <Icon
                                            className="blm-dropdown__cross"
                                            type="close_small"
                                            size={24}
                                        />
                                    ) : (
                                        <Icon
                                            type="arrow-down"
                                            size={24}
                                            button
                                            className="blm-dropdown__icon"
                                        />
                                    )}
                                </div>
                            </div>
                            <T.DropdownMenu {...getMenuProps()}>
                                <Div bulma="dropdown-content">
                                    <div ref={scrollableRef} className="blm-dropdown__scrollable">
                                        {filteredItems.map((item, index) => (
                                            <div
                                                key={getUniqKey(item)}
                                                {...getItemProps({
                                                    item,
                                                    index,
                                                    className: cn(
                                                        "is-highlight-on-hover dropdown-item blm-dropdown__item",
                                                        {
                                                            "is-hovered":
                                                                selectedItem?.id === item.id ||
                                                                highlightedIndex === index,
                                                        }
                                                    ),
                                                    style: { fontWeight: "normal" },
                                                })}
                                            >
                                                {getValue(item)}
                                            </div>
                                        ))}
                                    </div>

                                    {showEmpty && (
                                        <div className="text-center blm-autocomplete__not-found">
                                            <Text
                                                isSecondary
                                                className="blm-autocomplete__clear-text"
                                            >
                                                Ничего не найдено.
                                            </Text>
                                            <br />
                                            <Button
                                                isLikeLink
                                                onClick={() => {
                                                    reset()
                                                    onChange?.()
                                                }}
                                            >
                                                <Text
                                                    isSecondary
                                                    className="blm-autocomplete__clear-text is-underlined"
                                                >
                                                    Очистить поиск
                                                </Text>
                                            </Button>
                                        </div>
                                    )}
                                </Div>
                            </T.DropdownMenu>
                        </div>
                    </T.Dropdown>
                )
            }}
        </Downshift>
    )
}
